import type maplibregl from 'maplibre-gl';
import type { GeoTracker } from '../map/geolocation.js';

/** Static crosshair icon — a trusted constant, never interpolated with user data. */
const LOCATE_SVG =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round">' +
  '<circle cx="12" cy="12" r="7"/>' +
  '<line x1="12" y1="1" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="23"/>' +
  '<line x1="1" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="23" y2="12"/>' +
  '<circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none"/></svg>';

const RECENTER_ZOOM = 14;

export interface LocateButtonDeps {
  geo: GeoTracker;
  map: maplibregl.Map;
}

/** A corner button that (re)acquires location and flies the camera to the fix. */
export function createLocateButton(parent: HTMLElement, { geo, map }: LocateButtonDeps): { destroy(): void } {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'control-btn locate-btn';
  btn.setAttribute('aria-label', 'Center on my location');
  btn.innerHTML = LOCATE_SVG;
  parent.appendChild(btn);

  const sync = (): void => {
    const { status } = geo.getState();
    btn.dataset.status = status;
    if (status === 'denied') {
      btn.title = 'Location blocked — enable it in your browser settings';
    } else if (status === 'error') {
      btn.title = 'Location unavailable — tap to retry';
    } else {
      btn.title = 'Center on my location';
    }
  };
  const offSync = geo.onChange(sync);
  sync();

  const flyToFix = (): void => {
    const { fix } = geo.getState();
    if (fix) map.flyTo({ center: fix.coords, zoom: Math.max(map.getZoom(), RECENTER_ZOOM) });
  };

  let pendingOff: (() => void) | null = null;
  const onClick = (): void => {
    const { status } = geo.getState();
    if (status === 'denied' || status === 'error') {
      // The watch is still "active" from the browser's perspective even after
      // it reported a failure, so a plain start() would no-op. Force a clean
      // restart so a retry click can actually re-request a fix.
      geo.stop();
    }
    geo.start(); // idempotent: ensures we are watching
    if (geo.getState().fix) {
      flyToFix();
      return;
    }
    if (pendingOff) return; // already waiting for the first fix
    // No fix yet — recenter once, as soon as the first fix arrives.
    pendingOff = geo.onChange(() => {
      if (geo.getState().fix) {
        flyToFix();
        pendingOff?.();
        pendingOff = null;
      }
    });
  };
  btn.addEventListener('click', onClick);
  return { destroy() { pendingOff?.(); offSync(); btn.removeEventListener('click', onClick); } };
}
